import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ISensorenDashboardDataResponse, ISensorenDashboardFlowData } from '../models/dashboard';
import { ExportRequest } from '../models/ExportRequest';
import { StationHistoryChannelDto } from '../models/StationHistoryChannelDto';
import { StationOverviewDto } from '../models/StationOverviewDto';

@Injectable({
  providedIn: 'root',
})
export class NivusService {
  private readonly baseUrl = environment.apiUrl + 'Nivus/';

  constructor(private http: HttpClient) {}

  // Stations
  getStationsOverview(): Observable<StationOverviewDto[]> {
    return this.http.get<StationOverviewDto[]>(`${this.baseUrl}stations/overview`);
  }

  getStation(stationId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}stations/${stationId}`);
  }

  getStationHistory(stationId: string, from: string, until: string): Observable<StationHistoryChannelDto[]> {
    const params = new HttpParams().set('from', from).set('until', until);
    return this.http.get<StationHistoryChannelDto[]>(`${this.baseUrl}stations/${stationId}/history`, { params });
  }

  // Devices
  getDevices(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}devices`);
  }

  getDevice(serial: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}devices/${serial}`);
  }

  // Projects
  getProjectChannels(projectId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}projects/${projectId}/channels`);
  }

  // Channels
  getChannelHistory(channelId: string, from: string, until: string): Observable<StationHistoryChannelDto> {
    const params = new HttpParams().set('from', from).set('until', until);
    return this.http.get<StationHistoryChannelDto>(`${this.baseUrl}channels/${channelId}/history`, { params });
  }

  getChannelLive(channelId: string, from?: string, until?: string): Observable<any> {
    let params = new HttpParams();
    if (from) params = params.set('from', from);
    if (until) params = params.set('until', until);
    return this.http.get<any>(`${this.baseUrl}channels/${channelId}/live`, { params });
  }

  getChannelLiveData(channelId: string, from?: string, until?: string): Observable<any> {
    let params = new HttpParams();
    if (from) params = params.set('from', from);
    if (until) params = params.set('until', until);
    return this.http.get<any>(`${this.baseUrl}channels/${channelId}/live-data`, { params });
  }

  // Export
  exportCsv(stationId: string, request: ExportRequest): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}export/${stationId}/csv`, request);
  }

  exportXml(stationId: string, request: ExportRequest): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}export/${stationId}/xml`, request);
  }

  exportJson(stationId: string, request: ExportRequest): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}export/${stationId}/json`, request);
  }

  getExportTimezones(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}export/timezones`);
  }

  getExportCultures(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}export/cultures`);
  }

  // Cameras
  getCameraImage(cameraId: string, time?: string): Observable<any> {
    let params = new HttpParams();
    if (time) params = params.set('time', time);
    return this.http.get<any>(`${this.baseUrl}cameras/${cameraId}/image`, { params });
  }

  getDashboardData(from: string, until: string): Observable<ISensorenDashboardDataResponse> {
    const params = new HttpParams().set('from', from).set('until', until);
    return this.http.get<ISensorenDashboardDataResponse>(`${this.baseUrl}dashboard`, { params });
  }

  getTotalRainfall(from: string, until: string): Observable<ISensorenDashboardFlowData> {
    const params = new HttpParams().set('from', from).set('until', until);
    return this.http.get<ISensorenDashboardFlowData>(`${this.baseUrl}dashboard/total-rainfall`, { params });
  }
}
